import { createClient } from '@supabase/supabase-js'

// Supabase client configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Type definitions
export interface Event {
  id: string
  title: string
  description: string
  date: string
  location: string
  max_participants: number
  current_participants: number
  registration_deadline: string
  created_at: string
  updated_at: string
  goreg: 'yes' | 'no'
  // New fields from database update
  requirements?: string
  amenities?: string[]
  organizer?: string
  difficulty?: string
  entry_fee_from?: string
  image_url?: string
  route_url?: string
  // Payment gateway fields
  organizer_id?: string
  payment_gateway?: 'payfast' | 'paypal' | 'stripe' | 'manual'
  payment_config?: PaymentConfig
}

export interface PaymentConfig {
  // PayFast configuration
  merchant_id?: string
  merchant_key?: string
  passphrase?: string
  sandbox?: boolean
  // Flag to indicate if this is an admin configuration
  is_admin_config?: boolean
  // PayPal configuration
  paypal_client_id?: string
  paypal_client_secret?: string
  // Stripe configuration
  stripe_public_key?: string
  stripe_secret_key?: string
  // Manual payment details
  banking_details?: BankingDetails
}

export interface BankingDetails {
  bank_name: string
  account_holder: string
  account_number: string
  branch_code: string
  account_type: string
  reference_format: string
}

export interface EventOrganizer {
  id: string
  name: string
  email: string
  phone: string
  organization: string
  payment_gateway: 'payfast' | 'paypal' | 'stripe' | 'manual'
  payment_config: PaymentConfig
  is_verified: boolean
  created_at: string
}

export interface EventDistance {
  id: string
  event_id: string
  name: string
  length: string
  price: string
  description: string
  age_restriction: string
  difficulty: string
  created_at: string
}

export interface Registration {
  id: string
  event_id: string
  user_name: string
  user_email: string
  user_phone: string
  emergency_contact: string
  medical_conditions: string
  registration_date: string
  status: 'pending' | 'confirmed' | 'cancelled' | 'paid'
  // Payment tracking fields
  payment_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded'
  payment_method: 'payfast' | 'paypal' | 'stripe' | 'manual' | null
  payment_reference: string | null
  payment_amount: number
  payment_date: string | null
  distance_id: string
}

export interface PaymentTransaction {
  id: string
  registration_id: string
  event_id: string
  organizer_id: string
  amount: number
  currency: string
  payment_method: 'payfast' | 'paypal' | 'stripe' | 'manual'
  payment_reference: string
  gateway_transaction_id: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded'
  gateway_response: any
  created_at: string
  updated_at: string
}

export interface User {
  id: string
  email: string
  name: string
  phone: string
  date_of_birth: string
  gender: string
  rider_category: string
  csa_licence: string
  emergency_contact_name: string
  emergency_contact_phone: string
  emergency_contact_relationship: string
  allergies: string
  medical_conditions: string
  profile_image_url: string
  created_at: string
}

export interface Minor {
  id: string
  user_id: string
  name: string
  date_of_birth: string
  gender: string
  rider_category: string
  emergency_contact_name?: string
  emergency_contact_phone?: string
  emergency_contact_relationship?: string
  allergies?: string
  medical_conditions?: string
  csa_licence?: string
  created_at: string
}

// Coordinator Upgrade System Interfaces
export interface CoordinatorUpgrade {
  id: string
  user_id: string
  user_email: string
  user_name: string
  payment_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded'
  payment_method: string
  payment_reference: string
  payment_amount: number
  currency: string
  payfast_payment_id?: string
  payfast_transaction_id?: string
  gateway_response?: any
  created_at: string
  paid_at?: string
  updated_at: string
}

export interface CoordinatorCredits {
  id: string
  user_id: string
  user_email: string
  upgrade_id: string
  credits_purchased: number
  credits_used: number
  credits_remaining: number
  created_at: string
  updated_at: string
}

export interface CoordinatorProfile {
  id: string
  user_id: string
  user_email: string
  user_name: string
  role: 'coordinator' | 'admin'
  status: 'active' | 'suspended' | 'inactive'
  total_credits_purchased: number
  total_credits_used: number
  total_events_created: number
  first_upgrade_date?: string
  last_payment_date?: string
  created_at: string
  updated_at: string
}

export interface CoordinatorUpgradeRequest {
  user_email: string
  user_name: string
  return_url: string
  cancel_url: string
  notify_url: string
} 